import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../shared/context/AuthContext";
import { Button } from "../../../shared/components/ui/button";
import { Input } from "../../../shared/components/ui/input";
import { Label } from "../../../shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../shared/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../shared/components/ui/tabs";
import { toast } from "sonner";
import { Zap, Shield, Users, ClipboardList, HelpCircle } from "lucide-react";
import slbApi from "../services/slbApi";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);

  // Login state
  const [loginEmployeeId, setLoginEmployeeId] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register state
  const [regName, setRegName] = useState("");
  const [regEmployeeId, setRegEmployeeId] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regRole, setRegRole] = useState("worker");
  const [regPhone, setRegPhone] = useState("");
  const [regEmail, setRegEmail] = useState("");

  // Check SSO status on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await slbApi.get("/sso/status");
        setSsoEnabled(!!res.data?.enabled);
      } catch {
        setSsoEnabled(false);
      }
    })();
  }, []);

  const startSSO = () => {
    navigate("/station-logbook/sso/start?return_to=/");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginEmployeeId, loginPassword);
      toast.success("Login successful");
      navigate("/");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await register({
        name: regName,
        employee_id: regEmployeeId,
        password: regPassword,
        role: regRole,
        phone: regPhone || null,
        email: regEmail || null,
      });
      toast.success("Registration successful");
      navigate("/");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0F172A] dark:bg-[#09090B] flex-col items-center justify-center">
        <img 
          src="/logo.png" 
          alt="Station Log Book" 
          className="w-[700px] h-auto max-w-[90%]"
        />
      </div>

      {/* Right side - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img 
              src="/logo.png" 
              alt="Station Log Book" 
              className="w-[280px] h-auto"
            />
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 rounded-none h-12 p-1 bg-muted">
              <TabsTrigger
                value="login"
                className="rounded-none font-mono uppercase tracking-wider text-xs data-[state=active]:bg-background"
                data-testid="login-tab"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger
                value="register"
                className="rounded-none font-mono uppercase tracking-wider text-xs data-[state=active]:bg-background"
                data-testid="register-tab"
              >
                Register
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="login-employee-id"
                    className="font-mono text-xs uppercase tracking-widest"
                  >
                    Employee ID
                  </Label>
                  <Input
                    id="login-employee-id"
                    type="text"
                    value={loginEmployeeId}
                    onChange={(e) => setLoginEmployeeId(e.target.value)}
                    placeholder="EMP-001"
                    className="h-12 rounded-none border-2 font-mono"
                    required
                    data-testid="login-employee-id-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="login-password"
                    className="font-mono text-xs uppercase tracking-widest"
                  >
                    Password
                  </Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-12 rounded-none border-2 font-mono"
                    required
                    data-testid="login-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider font-bold"
                  data-testid="login-submit-btn"
                >
                  {isLoading ? (
                    <span className="spinner" />
                  ) : (
                    "Sign In"
                  )}
                </Button>

                {/* SSO Divider and Button */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-muted-foreground/20" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground font-mono tracking-wider">
                      or
                    </span>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={startSSO}
                  disabled={!ssoEnabled}
                  variant="outline"
                  className={`w-full h-12 rounded-sm font-mono uppercase tracking-wider border-2 ${
                    ssoEnabled 
                      ? "border-blue-500 text-blue-500 hover:bg-blue-500/10" 
                      : "border-muted-foreground/30 text-muted-foreground cursor-not-allowed"
                  }`}
                  data-testid="sso-login-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Sign in with GridGate SSO
                </Button>

                {!ssoEnabled && (
                  <p className="text-xs text-muted-foreground text-center font-mono mt-2">
                    SSO is not configured. Use manual login above.
                  </p>
                )}
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="reg-name"
                    className="font-mono text-xs uppercase tracking-widest"
                  >
                    Full Name
                  </Label>
                  <Input
                    id="reg-name"
                    type="text"
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    placeholder="John Smith"
                    className="h-12 rounded-none border-2 font-mono"
                    required
                    data-testid="register-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="reg-employee-id"
                    className="font-mono text-xs uppercase tracking-widest"
                  >
                    Employee ID
                  </Label>
                  <Input
                    id="reg-employee-id"
                    type="text"
                    value={regEmployeeId}
                    onChange={(e) => setRegEmployeeId(e.target.value)}
                    placeholder="EMP-001"
                    className="h-12 rounded-none border-2 font-mono"
                    required
                    data-testid="register-employee-id-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="reg-password"
                    className="font-mono text-xs uppercase tracking-widest"
                  >
                    Password
                  </Label>
                  <Input
                    id="reg-password"
                    type="password"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-12 rounded-none border-2 font-mono"
                    required
                    data-testid="register-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="reg-role"
                    className="font-mono text-xs uppercase tracking-widest"
                  >
                    Role
                  </Label>
                  <Select value={regRole} onValueChange={setRegRole}>
                    <SelectTrigger
                      className="h-12 rounded-none border-2 font-mono"
                      data-testid="register-role-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="worker">Substation Worker</SelectItem>
                      <SelectItem value="operator">Area Operator</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="reg-email"
                    className="font-mono text-xs uppercase tracking-widest"
                  >
                    Email (Optional)
                  </Label>
                  <Input
                    id="reg-email"
                    type="email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    placeholder="worker@utility.com"
                    className="h-12 rounded-none border-2 font-mono"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="reg-phone"
                    className="font-mono text-xs uppercase tracking-widest"
                  >
                    Phone (Optional - for SMS alerts)
                  </Label>
                  <Input
                    id="reg-phone"
                    type="tel"
                    value={regPhone}
                    onChange={(e) => setRegPhone(e.target.value)}
                    placeholder="+1234567890"
                    className="h-12 rounded-none border-2 font-mono"
                    data-testid="register-phone-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider font-bold"
                  data-testid="register-submit-btn"
                >
                  {isLoading ? (
                    <span className="spinner" />
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          
          {/* Help Link */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate("/manual")}
              className="text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-2 mx-auto transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
              Need help? View User Manual
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
